<script setup lang="ts">
import type { BaseButtonProps } from "~/types/BaseButton";

const {
  colorScheme = "primary",
  size = "regular",
  isLoading = false,
  disabled = false,
  is = "button",
  cta,
} = defineProps<BaseButtonProps>();

const interactionClass = computed(() => {
  const schemeClass = `scheme-${colorScheme}`;

  if (
    colorScheme === "secondary" ||
    colorScheme === "tertiary" ||
    colorScheme === "transparent"
  ) {
    return [schemeClass, "interaction-white"];
  }

  if (colorScheme === "primary") {
    return [schemeClass, "interaction-dark"];
  }

  return [schemeClass];
});

const ctaClass = computed(() => {
  if (cta) return "cta";

  return undefined;
});

const sizeClass = computed(() => {
  return `size-${size}`;
});

const isDisabled = computed(() => {
  if (is !== "button") return undefined;

  if (isLoading) return true;

  return disabled;
});

const buttonRef = ref<HTMLButtonElement>();
defineExpose({ buttonRef });
</script>

<template>
  <component
    :is="is"
    ref="buttonRef"
    class="base-button inline-flex items-center justify-center uppercase"
    :class="[colorScheme, interactionClass, ctaClass, sizeClass]"
    v-bind="$attrs"
    :disabled="isDisabled"
  >
    <BaseIcon
      v-if="isLoading"
      name="spinner"
      class="mr-3 flex justify-center"
      :size="20"
    />
    <slot />
  </component>
</template>

<style lang="postcss" scoped>
.base-button {
  &.size-small {
    @apply py-0 px-6;
  }

  &.size-medium {
    @apply py-3 px-2 text-sm;
  }

  &.size-responsive {
    @apply py-2 px-6;
    @apply text-xs md:text-base;
  }

  &.scheme-transparent {
    @apply bg-transparent border-platform-border-1 text-platform-text-1;
  }

  &.scheme-danger {
    @apply bg-platform-danger border-platform-danger text-white;
  }
}
</style>
